/* Leads list with filter, inline edit, CSV import, manual add. */

import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api";

interface Lead {
  id: string;
  company_name: string;
  website_url?: string;
  city?: string;
  country?: string;
  target_contact_name?: string;
  target_role?: string;
  target_contact_email?: string;
  source: string;
  pipeline_stage: string;
  lead_score?: number;
  notes?: string;
  updated_at: number;
}

const STAGES: Array<{ key: string; label: string }> = [
  { key: "",                     label: "All stages" },
  { key: "new_lead",             label: "New" },
  { key: "qualified",            label: "Qualified" },
  { key: "approved_for_outreach",label: "Approved" },
  { key: "contacted",            label: "Contacted" },
  { key: "follow_up_due",        label: "Follow-up" },
  { key: "replied",              label: "Replied" },
  { key: "call_booked",          label: "Call" },
  { key: "demo_completed",       label: "Demo" },
  { key: "pilot_offered",        label: "Pilot" },
  { key: "trial_active",         label: "Trial" },
  { key: "paid_pilot",           label: "Paid" },
  { key: "customer",             label: "Customer" },
  { key: "not_relevant",         label: "Not relevant" },
  { key: "do_not_contact",       label: "DNC" },
];

export function GrowthLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stage, setStage] = useState<string>("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listGrowthLeads(stage ? { stage } : {});
      setLeads((r.leads as Lead[]) ?? []);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stage]);

  const filtered = useMemo(() => {
    if (!q.trim()) return leads;
    const term = q.toLowerCase();
    return leads.filter((l) =>
      [l.company_name, l.target_contact_name, l.target_contact_email, l.city, l.target_role]
        .filter(Boolean)
        .some((s) => (s as string).toLowerCase().includes(term))
    );
  }, [leads, q]);

  return (
    <div>
      <div className="sg-growth-toolbar">
        <input
          className="sg-input"
          placeholder="Search company, contact, email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, maxWidth: 360 }}
        />
        <select className="sg-input" value={stage} onChange={(e) => setStage(e.target.value)}>
          {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button className="sg-btn sg-btn-sm" onClick={() => setImportOpen(true)}>Import CSV</button>
        <button className="sg-btn sg-btn-primary sg-btn-sm" onClick={() => setCreating(true)}>+ Add lead</button>
      </div>

      {loading ? (
        <div className="sg-empty">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="sg-empty sg-empty-cta">
          <strong>No leads yet.</strong>
          <span style={{ display: "block", marginTop: 6, color: "var(--sg-ink-3)" }}>
            Add a lead manually, or paste a CSV. The brief recommends 100 to start with — your first 30 high-quality outreaches come out of that pool.
          </span>
          <div className="sg-row" style={{ gap: 8, marginTop: 14, justifyContent: "center" }}>
            <button className="sg-btn sg-btn-primary sg-btn-sm" onClick={() => setCreating(true)}>+ Add lead</button>
            <button className="sg-btn sg-btn-sm" onClick={() => setImportOpen(true)}>Import CSV</button>
          </div>
        </div>
      ) : (
        <div className="sg-panel">
          <table className="sg-table sg-growth-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Contact</th>
                <th>Stage</th>
                <th>Score</th>
                <th>Source</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => <LeadRow key={l.id} lead={l} onUpdated={load} />)}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateLead onClose={() => setCreating(false)} onCreated={load} />}
      {importOpen && <ImportCsv onClose={() => setImportOpen(false)} onImported={load} />}
    </div>
  );
}

function LeadRow({ lead, onUpdated }: { lead: Lead; onUpdated: () => void }) {
  const [stage, setStage] = useState(lead.pipeline_stage);
  const [enriching, setEnriching] = useState(false);

  async function changeStage(next: string) {
    setStage(next);
    try {
      await api.updateGrowthLead({ id: lead.id, patch: { pipeline_stage: next } });
      onUpdated();
    } catch (err: any) {
      alert(err?.message ?? "Update failed");
      setStage(lead.pipeline_stage);
    }
  }

  async function enrich() {
    if (!lead.website_url) return;
    setEnriching(true);
    try {
      const r = await api.enrichGrowthLead({ id: lead.id });
      const e = r.enrichment;
      const summary = [
        `Score: ${e?.lead_score ?? "?"}/10`,
        e?.priority_level ? `Priority: ${e.priority_level}` : null,
        e?.lead_score_reason ? `\nReason:\n${e.lead_score_reason}` : null,
        e?.disqualification_reason ? `\nDisqualified: ${e.disqualification_reason}` : null,
      ].filter(Boolean).join("\n");
      alert("Enriched.\n\n" + summary);
      onUpdated();
    } catch (err: any) {
      alert(err?.message ?? "Enrich failed");
    } finally {
      setEnriching(false);
    }
  }

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{lead.company_name}</div>
        <div className="sg-muted" style={{ fontSize: 12 }}>
          {lead.website_url || "—"} · {lead.city || ""} {lead.country || ""}
        </div>
      </td>
      <td>
        {lead.target_contact_name ? (
          <>
            <div>{lead.target_contact_name}{lead.target_role ? `, ${lead.target_role}` : ""}</div>
            <div className="sg-muted" style={{ fontSize: 12 }}>{lead.target_contact_email || "—"}</div>
          </>
        ) : <span className="sg-muted">—</span>}
      </td>
      <td>
        <select
          className="sg-input"
          value={stage}
          onChange={(e) => changeStage(e.target.value)}
          style={{ fontSize: 12, padding: "4px 8px" }}
        >
          {STAGES.filter((s) => s.key).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </td>
      <td>{typeof lead.lead_score === "number" ? `${lead.lead_score}/10` : "—"}</td>
      <td><code style={{ fontSize: 11 }}>{lead.source}</code></td>
      <td className="sg-muted" style={{ fontSize: 12 }}>{new Date(lead.updated_at).toLocaleDateString()}</td>
      <td style={{ textAlign: "right" }}>
        {lead.website_url ? (
          <button className="sg-btn sg-btn-ghost sg-btn-sm" onClick={enrich} disabled={enriching}>
            {enriching ? "Enriching…" : "Enrich"}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function CreateLead({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [contact, setContact] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!companyName.trim()) return;
    setBusy(true);
    try {
      await api.createGrowthLead({
        company_name: companyName,
        website_url: website || undefined,
        target_contact_name: contact || undefined,
        target_role: role || undefined,
        target_contact_email: email || undefined,
        city: city || undefined,
        source: "manual",
      });
      onCreated();
      onClose();
    } catch (err: any) {
      alert(err?.message ?? "Failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="sg-modal-backdrop" onClick={onClose}>
      <div className="sg-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3 style={{ marginTop: 0 }}>Add lead</h3>
        <div className="sg-stack" style={{ gap: 10 }}>
          <input className="sg-input" placeholder="Company name *" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
          <input className="sg-input" placeholder="Website URL" value={website} onChange={(e) => setWebsite(e.target.value)} />
          <div className="sg-row" style={{ gap: 10 }}>
            <input className="sg-input" placeholder="Contact name" value={contact} onChange={(e) => setContact(e.target.value)} />
            <input className="sg-input" placeholder="Role" value={role} onChange={(e) => setRole(e.target.value)} />
          </div>
          <input className="sg-input" placeholder="Contact email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="sg-input" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div className="sg-row" style={{ marginTop: 16, justifyContent: "flex-end", gap: 8 }}>
          <button className="sg-btn sg-btn-ghost sg-btn-sm" onClick={onClose}>Cancel</button>
          <button className="sg-btn sg-btn-primary sg-btn-sm" disabled={busy || !companyName.trim()} onClick={submit}>
            {busy ? "Saving…" : "Add lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportCsv({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ inserted: number; skipped: number } | null>(null);

  async function handleSubmit() {
    let csv = paste;
    const f = fileRef.current?.files?.[0];
    if (f) csv = await f.text();
    if (!csv.trim()) return;
    setBusy(true);
    try {
      const rows = parseCsv(csv);
      const r = await api.importGrowthLeadsBulk({ rows });
      setResult({ inserted: r.insertedCount, skipped: r.skippedCount });
      onImported();
    } catch (err: any) {
      alert(err?.message ?? "Import failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="sg-modal-backdrop" onClick={onClose}>
      <div className="sg-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <h3 style={{ marginTop: 0 }}>Import CSV</h3>
        <div className="sg-muted" style={{ fontSize: 13, marginBottom: 10 }}>
          Required column: <code>company_name</code>. Recognised: <code>website_url</code>, <code>contact_name</code>, <code>contact_role</code>, <code>email</code>, <code>linkedin</code>, <code>phone</code>, <code>city</code>, <code>country</code>, <code>source</code>, <code>notes</code>.
        </div>
        <input type="file" ref={fileRef} accept=".csv,text/csv" />
        <div className="sg-muted" style={{ fontSize: 12, margin: "8px 0" }}>or paste rows (header on first line):</div>
        <textarea
          className="sg-textarea"
          rows={6}
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder="company_name,website_url,contact_name,email&#10;Acme Legal,https://acme.example,Sarah Patel,sarah@acme.example"
        />
        {result && (
          <div className="sg-muted" style={{ fontSize: 13, marginTop: 8 }}>
            Imported {result.inserted}. Skipped {result.skipped} (duplicates or invalid).
          </div>
        )}
        <div className="sg-row" style={{ marginTop: 16, justifyContent: "flex-end", gap: 8 }}>
          <button className="sg-btn sg-btn-ghost sg-btn-sm" onClick={onClose}>Close</button>
          <button className="sg-btn sg-btn-primary sg-btn-sm" disabled={busy} onClick={handleSubmit}>
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Cheap CSV parser. Handles quoted fields with embedded commas. No
 *  multi-line values (the founder's input is going to be Google-Sheets-clean). */
function parseCsv(text: string): any[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const header = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const out: any = {};
    header.forEach((h, i) => { if (cells[i] !== undefined) out[h] = cells[i].trim(); });
    return out;
  });
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; continue; }
    if (c === '"') { inQ = !inQ; continue; }
    if (c === "," && !inQ) { out.push(cur); cur = ""; continue; }
    cur += c;
  }
  out.push(cur);
  return out;
}
